import { Link } from 'react-router-dom';
import { ArrowRight, LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';

interface GlassCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  link: string;
  index?: number;
}

const GlassCard = ({ title, description, icon: Icon, link, index = 0 }: GlassCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }}
    >
      <Link to={link} className="block group h-full">
        <div 
          className="relative h-full p-8 rounded-2xl border border-white/10 overflow-hidden glass-card-inner"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--card) / 0.8) 0%, hsl(var(--muted) / 0.4) 100%)',
          }}
        >
          {/* Glow effect on hover */}
          <motion.div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: 'radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.15) 0%, transparent 60%)',
            }}
          />
          
          {/* Icon with glow */}
          <div className="relative z-10">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 relative glass-card-icon"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--primary) / 0.2) 0%, hsl(var(--primary) / 0.1) 100%)',
                border: '1px solid hsl(var(--primary) / 0.3)',
              }}
            >
              <Icon size={26} className="text-primary" />
            </div>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
              {title}
            </h3>
            
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              {description}
            </p>
            
            <div className="flex items-center gap-2 text-primary text-sm font-medium">
              <span>Learn more</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
          
          {/* Border glow effect */}
          <div
            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{
              boxShadow: 'inset 0 0 0 1px hsl(var(--primary) / 0.3)',
            }}
          />
        </div>
      </Link>
    </motion.div>
  );
};

export default GlassCard;